// engine/EntitySystem.ts — Structure-of-Arrays component store with a free-list
// id allocator and a compact active-id list.
//
// Design (per docs/EFFICIENCY.md §B):
//   - Entities are integer ids = indices into parallel typed arrays.
//   - spawn()/despawn() are O(1) and allocate nothing after construction.
//   - A free-list (LIFO stack of dead ids) recycles ids.
//   - A compact `activeIds` array lets systems iterate ONLY live entities in a
//     deterministic order (insertion order, with swap-remove), so tick cost
//     scales with population, not the MAX capacity.
//
// All component arrays are integer typed arrays so the state hash is exact
// (no float NaN/-0 ambiguity). Energy is Int32 by design.

import { KIND, NIL } from '../types.ts';
import type { EntityId, Kind } from '../types.ts';

/**
 * An opaque, deep capture of an EntitySystem's full state (DESIGN.md §6 —
 * snapshot/restore of the SoA store). Holds COPIES of the used range of every
 * component column plus the allocator (free-list) and active-set bookkeeping, so
 * restoring it reproduces the store exactly and subsequent ticks proceed
 * byte-identically. Produced by `snapshot()`, consumed by `restore()`. Treat it
 * as opaque — its shape is an implementation detail of EntitySystem.
 */
export interface EntitySnapshot {
  readonly capacity: number;
  readonly highWater: number;
  readonly freeCount: number;
  readonly activeCount: number;
  // Component columns (each sliced to [0, highWater) — the only meaningful range).
  readonly kind: Uint8Array;
  readonly state: Uint8Array;
  readonly posX: Int32Array;
  readonly posY: Int32Array;
  readonly energy: Int32Array;
  readonly foodAmount: Int32Array;
  readonly regenTimer: Int32Array;
  readonly alive: Uint8Array;
  readonly caste: Uint8Array;
  readonly colony: Int32Array;
  readonly hp: Int32Array;
  readonly cargo: Int32Array;
  readonly targetX: Int32Array;
  readonly targetY: Int32Array;
  readonly profileRef: Int32Array;
  readonly generation: Int32Array;
  readonly moveAccum: Int32Array;
  readonly blocks: Uint8Array;
  readonly dormant: Uint8Array;
  // Allocator + active-set state.
  readonly freeList: Int32Array;
  readonly activeIds: Int32Array;
  readonly activeSlot: Int32Array;
}

export class EntitySystem {
  readonly capacity: number;

  // --- Component columns (SoA). Index by EntityId. ---
  /** Entity kind (KIND.*). 0 (Empty) for dead/never-used slots. */
  readonly kind: Uint8Array;
  /** Behavioural state (AntState for ants; 0 otherwise). */
  readonly state: Uint8Array;
  readonly posX: Int32Array;
  readonly posY: Int32Array;
  /** Energy for ants (Int32, decays per tick). */
  readonly energy: Int32Array;
  /** Remaining amount for food nodes (Int32). */
  readonly foodAmount: Int32Array;
  /** Regen countdown for depleted food nodes (ticks remaining; 0 = active). */
  readonly regenTimer: Int32Array;
  /** 1 if the slot is a live entity, 0 otherwise. */
  readonly alive: Uint8Array;

  // --- Phase 2 organism columns (the shared "Organism" schema). ---
  // Unused by ants/food (they stay 0), so phase-1 behaviour is unchanged.
  /** Caste (CASTE.*) for KIND.Organism; 0 otherwise. */
  readonly caste: Uint8Array;
  /** Owning colony id for organisms; -1 (NIL) for non-members. */
  readonly colony: Int32Array;
  /** Hit points (combat) for organisms. */
  readonly hp: Int32Array;
  /** Food units currently carried (Worker/Specialist). */
  readonly cargo: Int32Array;
  /** Current movement target tile (patrol/return/explore). */
  readonly targetX: Int32Array;
  readonly targetY: Int32Array;
  /**
   * Genome profile snapshot id captured at spawn (see sim/genome.ts). Behaviours
   * read this organism's traits via the snapshot, so genome edits reach only
   * future spawns (non-retroactive). 0 for non-organisms.
   */
  readonly profileRef: Int32Array;
  /** Generation number this organism was born into (see sim/colony.ts). Stamped at spawn; existing units keep theirs when the colony's generation advances. 0 for non-organisms. */
  readonly generation: Int32Array;
  /**
   * Fixed-point sub-tick MOVEMENT ACCUMULATOR for the OPT-IN creature-speed feature
   * (see sim/genome.ts SPEED_UNIT + sim/castes.ts `advance`). Each tick (when the
   * feature is on) a creature accrues its `speed` here, spends whole SPEED_UNIT chunks
   * as steps, and KEEPS only the fractional remainder. With the feature OFF nothing
   * reads or writes it: it stays 0, draws no RNG, and is NOT folded into any state hash
   * unless a world opts in (computeColonyStateHash guards the fold on world.creatureSpeed),
   * so an off-mode world is byte-identical. Reset to 0 in spawn(). 0 for non-organisms.
   */
  readonly moveAccum: Int32Array;
  /**
   * 1 if this entity occupies a tile for collision (counts toward the per-tile
   * cap): ants and grounded organisms. 0 for food and Flyers (they don't block).
   * Defaulted from kind in spawn(); the colony layer overrides it for Flyers.
   */
  readonly blocks: Uint8Array;

  /**
   * Active/dirty sleeping flag (EFFICIENCY.md §B.6): 1 if a system has marked this
   * entity provably inert this tick so it may be SKIPPED. 0 = awake (the default
   * for every spawn). This is a PERF-ONLY annotation, NOT game state: it is OPT-IN
   * (nothing calls sleep() unless a system chooses to), it must only ever be set
   * when the per-tick work it skips is a genuine no-op, and it is deliberately NOT
   * folded into any state hash. With no caller using it, every world is byte-
   * identical. See sleep()/wake()/isDormant() and ResourceSystem for a consumer.
   */
  readonly dormant: Uint8Array;

  // --- Free-list allocator (LIFO stack of recycled ids). ---
  private readonly freeList: Int32Array;
  private freeCount: number;
  /** Next never-yet-allocated id (the high-water mark). */
  private highWater: number;

  // --- Compact active set for deterministic, population-scaled iteration. ---
  /** Dense array of live ids, [0, activeCount). */
  private readonly activeIds: Int32Array;
  /** activeCount of valid entries in activeIds. */
  private activeCount: number;
  /** Reverse map: entity id -> its slot index in activeIds (for swap-remove). */
  private readonly activeSlot: Int32Array;

  constructor(capacity: number) {
    if (capacity <= 0) throw new Error('EntitySystem capacity must be > 0');
    this.capacity = capacity;

    this.kind = new Uint8Array(capacity);
    this.state = new Uint8Array(capacity);
    this.posX = new Int32Array(capacity);
    this.posY = new Int32Array(capacity);
    this.energy = new Int32Array(capacity);
    this.foodAmount = new Int32Array(capacity);
    this.regenTimer = new Int32Array(capacity);
    this.alive = new Uint8Array(capacity);

    this.caste = new Uint8Array(capacity);
    this.colony = new Int32Array(capacity).fill(NIL);
    this.hp = new Int32Array(capacity);
    this.cargo = new Int32Array(capacity);
    this.targetX = new Int32Array(capacity);
    this.targetY = new Int32Array(capacity);
    this.profileRef = new Int32Array(capacity);
    this.generation = new Int32Array(capacity);
    this.moveAccum = new Int32Array(capacity); // sub-tick speed accumulator (feature opt-in)
    this.blocks = new Uint8Array(capacity);
    this.dormant = new Uint8Array(capacity); // all awake (0) by default

    this.freeList = new Int32Array(capacity);
    this.freeCount = 0;
    this.highWater = 0;

    this.activeIds = new Int32Array(capacity);
    this.activeCount = 0;
    this.activeSlot = new Int32Array(capacity).fill(-1);
  }

  /** Number of currently live entities. */
  get aliveCount(): number {
    return this.activeCount;
  }

  /**
   * Allocate a new entity id and mark it alive with the given kind/position.
   * O(1), allocation-free. Throws only if capacity is exhausted (a hard cap,
   * not a steady-state event). Component fields beyond kind/pos are reset to 0;
   * callers set energy/foodAmount as needed.
   */
  spawn(kind: Kind, x: number, y: number): EntityId {
    let id: EntityId;
    if (this.freeCount > 0) {
      id = this.freeList[--this.freeCount] as number;
    } else {
      if (this.highWater >= this.capacity) {
        throw new Error(`EntitySystem capacity ${this.capacity} exhausted`);
      }
      id = this.highWater++;
    }

    this.kind[id] = kind;
    this.state[id] = 0;
    this.posX[id] = x;
    this.posY[id] = y;
    this.energy[id] = 0;
    this.foodAmount[id] = 0;
    this.regenTimer[id] = 0;
    this.alive[id] = 1;

    // Reset organism columns. blocks defaults from kind (ants occupy tiles);
    // the colony layer overrides blocks for Flyers after spawn.
    this.caste[id] = 0;
    this.colony[id] = NIL;
    this.hp[id] = 0;
    this.cargo[id] = 0;
    this.targetX[id] = 0;
    this.targetY[id] = 0;
    this.profileRef[id] = 0;
    this.generation[id] = 0;
    this.moveAccum[id] = 0; // start with no carried sub-tile movement
    this.blocks[id] = kind === KIND.Ant ? 1 : 0;
    this.dormant[id] = 0; // fresh entities start awake; a system opts them to sleep

    // Append to the compact active set.
    const slot = this.activeCount++;
    this.activeIds[slot] = id;
    this.activeSlot[id] = slot;

    return id;
  }

  /**
   * Free an entity id (swap-remove from the active set, push to the free-list).
   * O(1), allocation-free. Safe to call on an already-dead id (no-op).
   */
  despawn(id: EntityId): void {
    if (this.alive[id] === 0) return;

    this.alive[id] = 0;
    this.kind[id] = KIND.Empty;

    // Swap-remove from activeIds to keep it dense.
    const slot = this.activeSlot[id] as number;
    const lastSlot = --this.activeCount;
    const movedId = this.activeIds[lastSlot] as number;
    this.activeIds[slot] = movedId;
    this.activeSlot[movedId] = slot;
    this.activeSlot[id] = -1;

    // Recycle the id.
    this.freeList[this.freeCount++] = id;
  }

  /** True if `id` currently refers to a live entity. */
  isAlive(id: EntityId): boolean {
    return this.alive[id] === 1;
  }

  /**
   * The backing dense array of live ids. Valid range is [0, aliveCount).
   * Iterate it directly for the deterministic active-set order. Do NOT mutate;
   * use spawn/despawn. (Despawn during iteration uses swap-remove, so a reverse
   * scan, or re-checking isAlive, is the safe pattern — see ants.ts.)
   */
  activeView(): Int32Array {
    return this.activeIds;
  }

  // --- Active/dirty sleeping (opt-in, perf-only; see the `dormant` column) -----

  /**
   * Mark `id` dormant so a cooperating system may skip its per-tick work. A
   * caller MUST only do this when that work is provably a no-op for the entity's
   * current state (else determinism breaks). No-op on a dead id. O(1).
   */
  sleep(id: EntityId): void {
    if (this.alive[id] === 0) return;
    this.dormant[id] = 1;
  }

  /** Clear `id`'s dormant flag (it must be processed again). No-op on dead. O(1). */
  wake(id: EntityId): void {
    if (this.alive[id] === 0) return;
    this.dormant[id] = 0;
  }

  /** True if `id` is currently marked dormant (and live). O(1). */
  isDormant(id: EntityId): boolean {
    return this.alive[id] === 1 && this.dormant[id] === 1;
  }

  // --- Snapshot / restore (out-of-band; DESIGN.md §6) --------------------------

  /**
   * Capture the full store state into an opaque, independent deep copy. Only the
   * used range [0, highWater) of each column is copied (the rest is provably
   * untouched: spawn writes a slot before it becomes live, despawn never reads
   * stale slots). Allocation is fine here — snapshot is an out-of-band save
   * operation, NOT part of the per-tick hot path. The result shares NO array
   * identity with this store, so later mutation cannot disturb it.
   */
  snapshot(): EntitySnapshot {
    const hw = this.highWater;
    return {
      capacity: this.capacity,
      highWater: hw,
      freeCount: this.freeCount,
      activeCount: this.activeCount,
      kind: this.kind.slice(0, hw),
      state: this.state.slice(0, hw),
      posX: this.posX.slice(0, hw),
      posY: this.posY.slice(0, hw),
      energy: this.energy.slice(0, hw),
      foodAmount: this.foodAmount.slice(0, hw),
      regenTimer: this.regenTimer.slice(0, hw),
      alive: this.alive.slice(0, hw),
      caste: this.caste.slice(0, hw),
      colony: this.colony.slice(0, hw),
      hp: this.hp.slice(0, hw),
      cargo: this.cargo.slice(0, hw),
      targetX: this.targetX.slice(0, hw),
      targetY: this.targetY.slice(0, hw),
      profileRef: this.profileRef.slice(0, hw),
      generation: this.generation.slice(0, hw),
      moveAccum: this.moveAccum.slice(0, hw),
      blocks: this.blocks.slice(0, hw),
      dormant: this.dormant.slice(0, hw),
      // freeList is meaningful in [0, freeCount); activeIds in [0, activeCount);
      // activeSlot is indexed by id, so copy its used [0, highWater) range.
      freeList: this.freeList.slice(0, this.freeCount),
      activeIds: this.activeIds.slice(0, this.activeCount),
      activeSlot: this.activeSlot.slice(0, hw),
    };
  }

  /**
   * Overwrite this store's state from a snapshot so subsequent ticks proceed
   * byte-identically to the original timeline. Copies back INTO the existing
   * arrays (never swaps identities — other systems hold references to these
   * columns). The snapshot's capacity must match this store's. Tail slots beyond
   * the snapshot's used range are cleared so no stale data can leak into a future
   * spawn that reuses those ids.
   */
  restore(snap: EntitySnapshot): void {
    if (snap.capacity !== this.capacity) {
      throw new Error(
        `EntitySnapshot capacity ${snap.capacity} != store capacity ${this.capacity}`,
      );
    }
    const hw = snap.highWater;
    this.highWater = hw;
    this.freeCount = snap.freeCount;
    this.activeCount = snap.activeCount;

    // Component columns: copy the captured range, then zero the tail so a slot
    // recycled by a future spawn never observes pre-snapshot data.
    this.kind.set(snap.kind);
    this.kind.fill(0, hw);
    this.state.set(snap.state);
    this.state.fill(0, hw);
    this.posX.set(snap.posX);
    this.posX.fill(0, hw);
    this.posY.set(snap.posY);
    this.posY.fill(0, hw);
    this.energy.set(snap.energy);
    this.energy.fill(0, hw);
    this.foodAmount.set(snap.foodAmount);
    this.foodAmount.fill(0, hw);
    this.regenTimer.set(snap.regenTimer);
    this.regenTimer.fill(0, hw);
    this.alive.set(snap.alive);
    this.alive.fill(0, hw);
    this.caste.set(snap.caste);
    this.caste.fill(0, hw);
    this.colony.set(snap.colony);
    this.colony.fill(NIL, hw);
    this.hp.set(snap.hp);
    this.hp.fill(0, hw);
    this.cargo.set(snap.cargo);
    this.cargo.fill(0, hw);
    this.targetX.set(snap.targetX);
    this.targetX.fill(0, hw);
    this.targetY.set(snap.targetY);
    this.targetY.fill(0, hw);
    this.profileRef.set(snap.profileRef);
    this.profileRef.fill(0, hw);
    this.generation.set(snap.generation);
    this.generation.fill(0, hw);
    this.moveAccum.set(snap.moveAccum);
    this.moveAccum.fill(0, hw);
    this.blocks.set(snap.blocks);
    this.blocks.fill(0, hw);
    this.dormant.set(snap.dormant);
    this.dormant.fill(0, hw);

    // Allocator + active set. Restore the used ranges; clear the tails so a stale
    // entry can never be mistaken for a live one.
    this.freeList.set(snap.freeList);
    this.freeList.fill(0, snap.freeCount);
    this.activeIds.set(snap.activeIds);
    this.activeIds.fill(0, snap.activeCount);
    this.activeSlot.set(snap.activeSlot);
    this.activeSlot.fill(-1, hw);
  }
}
